/**
 * pz-cart-pending-item.js — корзина выезжает СРАЗУ по «Добавить в корзину»,
 * пока сервер думает (PZ-554; идея Евгения: «показать сразу, а потом
 * догрузить контент с сервера»).
 *
 * Поток:
 *  1. pz:cart-add-sent (assets/pz-cart-add-watch.js) → открываем стоковую
 *     корзину (только если у неё auto-open, как в стоке) и ставим в список
 *     заглушку строки товара; у ПУСТОЙ корзины ещё заголовок и стоковый итог
 *     с «Pay» и «Go to cart» — они видны сразу (замечание Евгения 21.09);
 *  2. cart:update от product-form (товар добавлен) → заглушку убираем, её
 *     место занимает настоящее содержимое, которое морфит cart-items;
 *  3. cart:error (отказ) или pz:cart-add-failed (сеть, ответ не JSON) →
 *     заглушку убираем, в корзине — сообщение role="alert";
 *  4. страховка: ни ответа, ни события за SAFETY_TIMEOUT_MS → сообщение и
 *     перерисовка списка с сервера, чтобы показать правду.
 * Правила переходов — чистая функция nextPendingState (тесты:
 * .github/tools/test_pz_cart_pending_item.mjs).
 *
 * Морфинг. Корзина живёт внутри секции шапки, а на cart:update cart-items
 * морфит ВСЮ секцию: любой наш узел внутри dialog будет стёрт. Поэтому
 * заглушка и сообщение — это СОСТОЯНИЕ, а разметку по нему восстанавливает
 * MutationObserver (render() идемпотентна: уже на месте — не трогает).
 *
 * Быстрое добавление (quick-add) не трогаем: его окно тоже модальное, и две
 * одновременные showDialog ломают блокировку прокрутки страницы (ревью
 * архитектора 21.09) — там остаётся стоковое поведение.
 *
 * Известное ограничение: POST на /cart/add.js от стороннего приложения тоже
 * откроет корзину; без cart:update заглушку снимет страховка через 15 с.
 * Сейчас на этот адрес ходит только product-form.
 */

// Имена событий задаёт assets/pz-cart-add-watch.js — менять вместе. Модуль
// не импортируем: адрес без ?v= Shopify кэширует на год (см. шапку watch)
// (совпадение сверяет тест .github/tools/test_pz_cart_pending_item.mjs)
export const CART_ADD_SENT_EVENT = 'pz:cart-add-sent';
export const CART_ADD_FAILED_EVENT = 'pz:cart-add-failed';

// Ответ сервера на добавление — 0,8–1,5 с (замер 21.09); 15 с = точно сбой
const SAFETY_TIMEOUT_MS = 15000;
const ERROR_VISIBLE_MS = 6000;
const PRODUCT_FORM_SOURCE = 'product-form-component';
const PENDING_ITEM_TEMPLATE_ID = 'pz-cart-pending-item-template';
const ADD_ERROR_TEMPLATE_ID = 'pz-cart-add-error-template';
const PENDING_ITEM_SELECTOR = '[data-pz-cart-pending-item]';
const PENDING_STATUS_SELECTOR = '[data-pz-cart-pending-status]';
const ADD_ERROR_SELECTOR = '[data-pz-cart-add-error]';
const PENDING_HEADING_TEMPLATE_ID = 'pz-cart-pending-heading-template';
const PENDING_SUMMARY_TEMPLATE_ID = 'pz-cart-pending-summary-template';
const PENDING_HEADING_SELECTOR = '[data-pz-cart-pending-heading]';
const PENDING_SUMMARY_SELECTOR = '[data-pz-cart-pending-summary]';
// Итог, пришедший с сервера (не наш), — признак непустой корзины
const SERVER_SUMMARY_SELECTOR = '.cart-drawer__summary:not([data-pz-cart-pending-summary])';

/**
 * @typedef {{ pendingCount: number, errorText: string }} PendingState
 * @typedef {{ type: 'sent' }
 *   | { type: 'updated', source?: string, didError?: boolean }
 *   | { type: 'rejected', description?: unknown, message?: string, fallbackText: string }
 *   | { type: 'failed', fallbackText: string }
 *   | { type: 'timeout', fallbackText: string }
 *   | { type: 'errorExpired' }} PendingEvent
 */

/** Снять одну заглушку с заданным текстом ошибки. */
const finishOne = (state, errorText) => ({ pendingCount: Math.max(0, state.pendingCount - 1), errorText });

/** Текст отказа: у Shopify в message общее «Cart Error», понятный текст — в description. */
function rejectionText({ description, message, fallbackText }) {
  return typeof description === 'string' && description ? description : message || fallbackText;
}

/**
 * Таблица переходов: тип события → новое состояние. Сигнал без ожидающего
 * запроса (чужое обновление корзины, повторный сигнал) состояние не меняет —
 * так не снимется чужая заглушка.
 * @type {Record<string, (state: PendingState, event: any) => PendingState>}
 */
const PENDING_TRANSITIONS = {
  sent: (state) => ({ pendingCount: state.pendingCount + 1, errorText: '' }),
  // didError: об отказе уже сообщил cart:error, строку сняли там
  updated: (state, event) =>
    state.pendingCount > 0 && event.source === PRODUCT_FORM_SOURCE && !event.didError ? finishOne(state, '') : state,
  rejected: (state, event) => (state.pendingCount > 0 ? finishOne(state, rejectionText(event)) : state),
  failed: (state, event) => (state.pendingCount > 0 ? finishOne(state, event.fallbackText) : state),
  timeout: (state, event) => (state.pendingCount > 0 ? { pendingCount: 0, errorText: event.fallbackText } : state),
  errorExpired: (state) => ({ ...state, errorText: '' }),
};

/**
 * Переходы состояния заглушки. Чистая функция: без DOM и таймеров.
 * @param {PendingState} state
 * @param {PendingEvent} event
 * @returns {PendingState}
 */
export function nextPendingState(state, event) {
  const transition = PENDING_TRANSITIONS[event.type];
  return transition ? transition(state, event) : state;
}

let state = { pendingCount: 0, errorText: '' };
let isOpening = false;
let variantImage = '';
let announcedErrorText = '';
let safetyTimer = 0;
let errorTimer = 0;
let isRenderScheduled = false;
let morphWatcher = null;

const findDrawer = () => document.querySelector('cart-drawer-component');
const findItemsList = () => findDrawer()?.querySelector('dialog .cart-drawer__items') ?? null;
const isQuickAddOpen = () => Boolean(document.querySelector('#quick-add-dialog dialog[open]'));
const fallbackErrorText = () => document.getElementById(ADD_ERROR_TEMPLATE_ID)?.dataset.fallbackText || '';

/** Открывает стоковую корзину один раз, даже при двойном нажатии. */
function openDrawer() {
  const drawer = findDrawer();
  const dialog = drawer?.querySelector('dialog');
  if (!drawer || !dialog || !drawer.hasAttribute('auto-open') || typeof drawer.showDialog !== 'function') return false;
  if (dialog.open || isOpening) return true;

  // showDialog проверяет dialog.open синхронно, а showModal зовёт в следующем
  // кадре: два вызова в одном кадре открыли бы корзину дважды
  isOpening = true;
  drawer.showDialog();
  requestAnimationFrame(() => requestAnimationFrame(() => (isOpening = false)));
  return true;
}

/** Картинка варианта, который добавляют, — со страницы, если она там есть. */
function findVariantImage(variantId) {
  if (!variantId) return '';
  const input = document.querySelector(`product-form-component input[name="id"][value="${CSS.escape(variantId)}"]`);
  const button = input?.closest('product-form-component')?.querySelector('add-to-cart-component[data-product-variant-media]');
  return button?.dataset.productVariantMedia || '';
}

function createFromTemplate(templateId) {
  const template = document.getElementById(templateId);
  return template ? /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true)) : null;
}

/**
 * Живая область объявляет текст, только если он появился ПОСЛЕ вставки
 * элемента в документ, — поэтому текст ставим в следующем кадре.
 */
function announceNextFrame(element, text) {
  requestAnimationFrame(() => (element.textContent = text));
}

function syncPendingItem(list) {
  const existing = list.querySelector(PENDING_ITEM_SELECTOR);
  if (state.pendingCount === 0) return existing?.remove();
  if (existing) return;

  const item = createFromTemplate(PENDING_ITEM_TEMPLATE_ID);
  if (!item) return;
  const image = item.querySelector('img');
  if (image && variantImage) {
    image.src = variantImage;
    image.hidden = false;
  }
  const status = item.querySelector(PENDING_STATUS_SELECTOR);
  const statusText = status?.textContent ?? '';
  if (status) status.textContent = '';
  list.prepend(item);
  if (status) announceNextFrame(status, statusText);
}

function syncAddError(list) {
  const existing = list.querySelector(ADD_ERROR_SELECTOR);
  if (!state.errorText) {
    announcedErrorText = '';
    return existing?.remove();
  }
  if (existing?.textContent === state.errorText) return;

  const message = existing ?? createFromTemplate(ADD_ERROR_TEMPLATE_ID);
  if (!message) return;
  if (!existing) list.prepend(message);

  if (state.errorText === announcedErrorText) {
    // Возврат после морфинга шапки: уже объявлено — второй раз не кричим
    message.removeAttribute('role');
    message.textContent = state.errorText;
    return;
  }
  announcedErrorText = state.errorText;
  announceNextFrame(message, state.errorText);
}

/** Узел из шаблона есть, только пока нужен; вставка — prepend или append. */
function syncTemplateNode(container, { selector, templateId, shouldExist, position, prepare }) {
  if (!container) return;
  const existing = container.querySelector(selector);
  if (!shouldExist) return existing?.remove();
  if (existing) return;

  const node = createFromTemplate(templateId);
  if (!node) return;
  prepare?.(node);
  container[position](node);
}

/**
 * Итог в шаблоне отрисован для пустой корзины — «Pay» пришёл с disabled и
 * был бы тусклым, а потом вспыхнул бы оранжевым. Оставляем яркой: нажать её
 * до ответа сервера не даёт inert на итоге (syncSummaryLock).
 */
function showCheckoutAsReady(summary) {
  summary.querySelector('button[name="checkout"]')?.removeAttribute('disabled');
}

/**
 * Пустая корзина: заголовка и итога в её разметке нет — ставим свои, пока
 * ждём сервер. Ответ сервера морфингом заменит их настоящими.
 */
function syncEmptyCartFrame(dialog) {
  const shouldExist = state.pendingCount > 0 && !dialog.querySelector(SERVER_SUMMARY_SELECTOR);
  syncTemplateNode(dialog.querySelector('.cart-drawer__header'), {
    selector: PENDING_HEADING_SELECTOR,
    templateId: PENDING_HEADING_TEMPLATE_ID,
    shouldExist,
    position: 'prepend',
  });
  syncTemplateNode(dialog.querySelector('.cart-drawer__content'), {
    selector: PENDING_SUMMARY_SELECTOR,
    templateId: PENDING_SUMMARY_TEMPLATE_ID,
    shouldExist,
    position: 'append',
    prepare: showCheckoutAsReady,
  });
}

/**
 * Пока ждём сервер, итог (наш или настоящий) inert: «Pay» и «Go to cart»
 * видны, но ни мышь, ни клавиатура не уйдут к оплате без нового товара.
 * В серверной разметке атрибута нет — морфинг снимает его сам.
 */
function syncSummaryLock(dialog) {
  const isWaiting = state.pendingCount > 0;
  dialog.querySelectorAll('.cart-drawer__summary').forEach((summary) => summary.toggleAttribute('inert', isWaiting));
}

/** Приводит разметку корзины к состоянию. Идемпотентна. */
function render() {
  isRenderScheduled = false;
  const dialog = findDrawer()?.querySelector('dialog');
  const list = findItemsList();
  if (dialog) {
    syncEmptyCartFrame(dialog);
    syncSummaryLock(dialog);
  }
  if (list) {
    syncPendingItem(list);
    syncAddError(list);
    if (state.pendingCount > 0) list.setAttribute('aria-busy', 'true');
    else list.removeAttribute('aria-busy');
  }
  watchMorphs(state.pendingCount > 0 || Boolean(state.errorText));
}

function scheduleRender() {
  if (isRenderScheduled) return;
  isRenderScheduled = true;
  requestAnimationFrame(render);
}

/** Пока есть что показывать — возвращаем разметку после чужого морфинга. */
function watchMorphs(isActive) {
  const drawer = findDrawer();
  if (isActive && drawer && !morphWatcher) {
    morphWatcher = new MutationObserver(scheduleRender);
    morphWatcher.observe(drawer, { childList: true, subtree: true });
  } else if (!isActive && morphWatcher) {
    morphWatcher.disconnect();
    morphWatcher = null;
  }
}

/** Перерисовать список с сервера — показать правду после сбоя. */
async function refreshCartItems() {
  const sectionId = findDrawer()?.querySelector('cart-items-component')?.dataset.sectionId;
  if (!sectionId) return;
  const { sectionRenderer } = await import('@theme/section-renderer');
  sectionRenderer.renderSection(sectionId, { cache: false });
}

/** Применяет переход, заводит таймеры по новому состоянию и перерисовывает. */
function dispatch(event) {
  const previous = state;
  state = nextPendingState(state, event);
  if (state === previous) return;

  if (state.pendingCount === 0) clearTimeout(safetyTimer);
  if (state.errorText && state.errorText !== previous.errorText) {
    clearTimeout(errorTimer);
    errorTimer = window.setTimeout(() => dispatch({ type: 'errorExpired' }), ERROR_VISIBLE_MS);
  }
  render();
}

function handleAddSent(event) {
  if (isQuickAddOpen() || !openDrawer()) return;
  variantImage = findVariantImage(event.detail?.variantId);
  clearTimeout(safetyTimer);
  safetyTimer = window.setTimeout(() => {
    dispatch({ type: 'timeout', fallbackText: fallbackErrorText() });
    refreshCartItems();
  }, SAFETY_TIMEOUT_MS);
  dispatch({ type: 'sent' });
}

function handleAddFailed() {
  const wasWaiting = state.pendingCount > 0;
  dispatch({ type: 'failed', fallbackText: fallbackErrorText() });
  if (wasWaiting) refreshCartItems();
}

if (typeof document !== 'undefined') {
  document.addEventListener(CART_ADD_SENT_EVENT, handleAddSent);
  document.addEventListener(CART_ADD_FAILED_EVENT, handleAddFailed);
  document.addEventListener('cart:update', (event) => {
    const data = event.detail?.data;
    dispatch({ type: 'updated', source: data?.source, didError: data?.didError });
  });
  document.addEventListener('cart:error', (event) => {
    const data = event.detail?.data ?? {};
    dispatch({ type: 'rejected', description: data.description, message: data.message, fallbackText: fallbackErrorText() });
  });
}
